import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  List,
  message,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  MailOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import {
  fetchWorkspace,
  fetchWorkspaceMembershipsForUser,
  respondToWorkspaceInvitation,
} from "services/workspaceService";
import {
  formatDateTime,
  membershipUserEmail,
  membershipUserName,
  readable,
  statusColor,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceStyles } from "./colors";

const { Text, Title } = Typography;

const WorkspaceInvitations = () => {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [invitations, setInvitations] = useState([]);
  const [accountLookup, setAccountLookup] = useState({});
  const [loading, setLoading] = useState(false);
  const [processingKey, setProcessingKey] = useState("");

  const pendingCount = useMemo(() => invitations.length, [invitations]);

  const loadInvitations = useCallback(async ({ quiet = false } = {}) => {
    if (!actorId) return;

    try {
      if (!quiet) setLoading(true);

      const page = await fetchWorkspaceMembershipsForUser({
        actorId,
        userId: actorId,
        size: 100,
      });
      const pending = page.content.filter(
        (item) => String(item.membershipStatus || "").toUpperCase() === "PENDING",
      );
      setInvitations(pending);

      const entries = await Promise.all(
        pending
          .map((item) => item.workspaceId)
          .filter(Boolean)
          .map(async (workspaceId) => {
            try {
              const account = await fetchWorkspace(workspaceId, actorId);
              return [workspaceId, account];
            } catch {
              return [workspaceId, { id: workspaceId, name: "Workspace" }];
            }
          }),
      );
      setAccountLookup(Object.fromEntries(entries));
    } catch (error) {
      message.error(error?.message || "Failed to load workspace invitations.");
    } finally {
      setLoading(false);
    }
  }, [actorId]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const respond = async (invitation, action) => {
    if (!invitation?.workspaceId || !actorId || processingKey) return;

    const key = `${action}-${invitation.id || invitation.workspaceId}`;
    try {
      setProcessingKey(key);
      await respondToWorkspaceInvitation({
        actorId,
        workspaceId: invitation.workspaceId,
        action,
      });
      message.success(`Invitation ${action === "accept" ? "accepted" : "declined"}.`);
      await loadInvitations({ quiet: true });
    } catch (error) {
      message.error(error?.message || `Failed to ${action} invitation.`);
    } finally {
      setProcessingKey("");
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <div>
          <Title level={4} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebGreen }}>
            Invitations
          </Title>
          <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
            Accept or decline pending workspace invitations.
          </Text>
        </div>
        <Space>
          <Tag color={pendingCount ? "warning" : "default"}>{pendingCount} pending</Tag>
          <Button icon={<ReloadOutlined />} onClick={() => loadInvitations()}>
            Refresh
          </Button>
        </Space>
      </div>

      {loading && invitations.length === 0 ? (
        <Card>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : invitations.length === 0 ? (
        <Card>
          <Empty
            image={<InboxOutlined style={{ fontSize: 42, color: OLIVEEB_COLORS.oliveebBlue }} />}
            description="No pending invitations"
          />
        </Card>
      ) : (
        <List
          grid={{ gutter: 12, xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }}
          dataSource={invitations}
          renderItem={(invitation) => {
            const account = invitation.workspaceId
              ? accountLookup[invitation.workspaceId]
              : null;
            const acceptKey = `accept-${invitation.id || invitation.workspaceId}`;
            const declineKey = `decline-${invitation.id || invitation.workspaceId}`;
            const invitedAt = formatDateTime(invitation.createdAt);

            return (
              <List.Item>
                <Card
                  size="small"
                  style={{
                    borderRadius: 8,
                    borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
                    height: "100%",
                  }}
                  styles={{ body: { display: "flex", flexDirection: "column", gap: 12 } }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        display: "grid",
                        placeItems: "center",
                        background: workspaceStyles.iconBubble.background,
                        color: workspaceStyles.iconBubble.color,
                        flex: "0 0 auto",
                      }}
                    >
                      <MailOutlined />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text
                        strong
                        style={{
                          display: "block",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {account?.name || "Workspace"}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {readable(account?.product, "Product")} •{" "}
                        {readable(account?.accountType, "Workspace type")}
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      border: `1px solid ${OLIVEEB_COLORS.border}`,
                      borderRadius: 8,
                      background: OLIVEEB_COLORS.bgSoft,
                      padding: 10,
                    }}
                  >
                    <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                      Member
                    </Text>
                    <Text strong>{membershipUserName(invitation)}</Text>
                    {membershipUserEmail(invitation) ? (
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        {membershipUserEmail(invitation)}
                      </Text>
                    ) : null}
                  </div>

                  <Space wrap>
                    <Tag color={statusColor(invitation.membershipStatus)}>
                      {readable(invitation.membershipStatus, "Pending")}
                    </Tag>
                    {(invitation.roleNames || []).map((role) => (
                      <Tag key={role} color="blue">
                        {readable(role)}
                      </Tag>
                    ))}
                  </Space>

                  {invitedAt ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Invited {invitedAt}
                    </Text>
                  ) : null}

                  <Space wrap style={{ marginTop: "auto" }}>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      loading={processingKey === declineKey}
                      disabled={Boolean(processingKey)}
                      onClick={() => respond(invitation, "decline")}
                    >
                      Decline
                    </Button>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      loading={processingKey === acceptKey}
                      disabled={Boolean(processingKey)}
                      onClick={() => respond(invitation, "accept")}
                    >
                      Accept
                    </Button>
                  </Space>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </Space>
  );
};

export default WorkspaceInvitations;
